import { useState, useEffect, useCallback } from 'react';
import {
  collection, query, where, onSnapshot,
  addDoc, updateDoc, deleteDoc, doc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

const SHELVES = 'shelves';

/** 사용자가 만든 책장(태그). 책은 shelfIds 배열로 여러 책장에 속할 수 있다. */
export const useShelves = (userId) => {
  const [shelves, setShelves] = useState([]);

  useEffect(() => {
    if (!userId) {
      setShelves([]);
      return;
    }

    const q = query(collection(db, SHELVES), where('userId', '==', userId));
    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const next = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        next.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name));
        setShelves(next);
      },
      error => console.error('Failed to subscribe shelves:', error)
    );

    return () => unsubscribe();
  }, [userId]);

  const addShelf = useCallback(async (name) => {
    const trimmed = name?.trim();
    if (!userId) throw new Error('로그인이 필요합니다.');
    if (!trimmed) throw new Error('책장 이름을 입력해주세요.');
    if (shelves.some(s => s.name === trimmed)) throw new Error('같은 이름의 책장이 이미 있어요.');

    const ref = await addDoc(collection(db, SHELVES), {
      userId,
      name: trimmed,
      order: shelves.length,
      createdAt: serverTimestamp()
    });
    return ref.id;
  }, [userId, shelves]);

  const renameShelf = useCallback(async (shelfId, name) => {
    const trimmed = name?.trim();
    if (!trimmed) throw new Error('책장 이름을 입력해주세요.');
    await updateDoc(doc(db, SHELVES, shelfId), { name: trimmed });
  }, []);

  const deleteShelf = useCallback(async (shelfId) => {
    await deleteDoc(doc(db, SHELVES, shelfId));
  }, []);

  return { shelves, addShelf, renameShelf, deleteShelf };
};
